import { useCallback, useMemo } from 'react';

import { Box, Header } from '@rbilabs/universal-components';
import { ItemAllergens } from 'features/menu/components/product-detail/product-nutrition-information/item-allergens';
import { ItemIngredients } from 'features/menu/components/product-detail/product-nutrition-information/item-ingredients-information';
import { IngredientsType } from 'features/menu/components/product-detail/product-nutrition-information/types';

import { NutrientList } from 'components/nutrition/nutrient-list';
import { useNutrientItems } from 'components/nutrition/use-nutrient-items';
import { TableHeader } from 'components/nutrition/v2/table-header';
import Picture from 'components/picture';
import { useLocale } from 'state/intl';
import { t } from 'state/intl/translations';
import { ILocalizedMenuEntry } from 'state/unified-menu/types';
import { useUnifiedMenuContext } from 'state/unified-menu/unified-menu-context';
import { mapImageResourceToSanityImage } from 'state/unified-menu/utils';

type ItemNutritionInformationProps = {
  item: ILocalizedMenuEntry;
};

export const ItemNutritionInformation = ({ item }: ItemNutritionInformationProps) => {
  const { ingredients } = useUnifiedMenuContext();
  const { language } = useLocale();

  // This is used to check if the item is a non-food item
  // we don't want to show the per serving header and nutrients for non-food items
  const isNoNFood = item.productType && item.productType[0] === 'NON_FOOD';

  // This function is used to find the ingredients for the item
  const findIngredients = useCallback(() => {
    const finalIngredients = item.ingredients?.reduce((acc: any[], ingredientEntry) => {
      const found = ingredients?.find(ingredient => ingredient.id === ingredientEntry.entryId);
      if (found) {
        acc.push(found);
      }
      return acc;
    }, []);

    const finalIngredientsWithComponents = finalIngredients?.map(ingredient => ({
      name: ingredient?.name?.locale || ingredient?.name[language] || ingredient?.name?.en || '',
      ingredients: ingredient?.components[language] || ingredient?.components?.en || [],
    }));
    return (finalIngredientsWithComponents || []) as IngredientsType[];
  }, [item.ingredients, ingredients, language]);

  // Flatten the gateway nutrition data into a simple Record for the hook.
  // Calories lives at `item.nutrition.calories.def` instead of inside `nutrients`.
  const flatNutrients = useMemo(() => {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { __typename, ...rest } = item.nutrition?.nutrients || {};
    const base: Record<string, number | null | undefined> = { ...rest };

    if (!isNoNFood) {
      base.calories = item.nutrition?.calories?.def ?? 0;
    }
    return base;
  }, [item.nutrition, isNoNFood]);

  const nutrients = useNutrientItems(flatNutrients);

  return (
    <Box>
      <Picture
        image={mapImageResourceToSanityImage(item.image?.resource ?? '')}
        alt="itemImage"
        objectFitContain
        width={300}
        height={300}
        alignSelf="center"
      />
      <Box marginBottom="$4">
        <Header textAlign="center" focusable>
          {item.name?.locale}
        </Header>
        {!isNoNFood && <TableHeader testID="per-serving-header" title={t('perServing')} />}
        <NutrientList nutrients={nutrients} />
        <ItemIngredients ingredients={findIngredients()} />
        {item.nutrition?.allergens && <ItemAllergens item={item} />}
      </Box>
    </Box>
  );
};
